import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FiArrowLeft,
  FiActivity,
  FiRefreshCw,
  FiCheckCircle,
  FiPackage,
  FiFileText,
  FiAlertTriangle,
  FiTrendingUp,
  FiLayers,
  FiCpu,
  FiSearch,
  FiDownload,
  FiChevronLeft,
  FiChevronRight,
  FiPrinter,
} from 'react-icons/fi';
import {
  batchProcessApprovedPrs,
  convertPurchaseRequisitionToPo,
  getPoSummaryReport,
  listPurchaseOrders,
  listPurchaseRequisitions,
  syncPurchaseRequisitions,
} from '../../utils/procureApi';
import '../../Styles/ProcureAI.css';

const PR_STATUS_OPTIONS = ['APPROVED', 'PENDING', 'REJECTED', 'CONVERTED_TO_PO'];
const PO_STATUS_OPTIONS = ['CREATED', 'PENDING_APPROVAL', 'CANCELLED'];

const TABS = [
  { id: 'requisitions', label: 'Purchase Requisitions', icon: <FiFileText size={15} /> },
  { id: 'orders', label: 'Purchase Orders', icon: <FiPackage size={15} /> },
  { id: 'summary', label: 'Summary', icon: <FiActivity size={15} /> },
];

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.16, ease: 'easeIn' } },
};

function formatCurrency(amount, currency) {
  if (amount === null || amount === undefined) {
    return '-';
  }
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_) {
    return `${currency || ''} ${amount}`.trim();
  }
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatQuantity(value) {
  if (value === null || value === undefined) {
    return '-';
  }
  return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function toCsv(rows, columns) {
  const header = columns.map((column) => `"${column.label.replaceAll('"', '""')}"`).join(',');
  const body = rows
    .map((row) =>
      columns
        .map((column) => {
          const rawValue = column.value(row);
          const value = rawValue === null || rawValue === undefined ? '' : String(rawValue);
          return `"${value.replaceAll('"', '""')}"`;
        })
        .join(',')
    )
    .join('\n');
  return `${header}\n${body}`;
}

function downloadCsvFile(fileName, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function parseReasoningSections(reasoning) {
  if (!reasoning || typeof reasoning !== 'string') {
    return [];
  }

  const lines = reasoning.split('\n');
  const sections = [];
  let current = null;

  const pushCurrent = () => {
    if (!current) {
      return;
    }
    const content = current.content.join('\n').trim();
    sections.push({ title: current.title.trim(), content });
  };

  lines.forEach((line) => {
    const markdownHeadingMatch = line.match(/^###\s+(.+)/);
    const numericHeadingMatch = line.match(/^(\d+\.\s+.+)/);

    if (markdownHeadingMatch) {
      pushCurrent();
      current = { title: markdownHeadingMatch[1], content: [] };
      return;
    }

    if (numericHeadingMatch) {
      pushCurrent();
      current = { title: numericHeadingMatch[1], content: [] };
      return;
    }

    if (!current) {
      current = { title: 'Analysis', content: [] };
    }
    current.content.push(line);
  });

  pushCurrent();
  return sections.filter((section) => section.content.length > 0 || section.title);
}

function getAnalysisChecks(aiAnalysis) {
  if (!aiAnalysis) {
    return [];
  }

  const checks = [
    {
      key: 'source_list_check',
      label: 'Source List',
      payload: aiAnalysis.source_list_check,
    },
    {
      key: 'pricing_check',
      label: 'Pricing',
      payload: aiAnalysis.pricing_check,
    },
    {
      key: 'contract_check',
      label: 'Contract',
      payload: aiAnalysis.contract_check,
    },
  ];

  return checks
    .filter((item) => item.payload)
    .map((item) => {
      const isPassed = item.payload.passed === true;
      const isFailed = item.payload.passed === false;
      return {
        key: item.key,
        label: item.label,
        message: item.payload.message || '',
        status: isPassed ? 'pass' : isFailed ? 'fail' : 'warn',
      };
    });
}

function getDecisionSummary(po) {
  if (!po) {
    return null;
  }

  const confidence = po.ai_analysis?.confidence_score;
  const hasExceptions = (po.ai_analysis?.exceptions || []).length > 0;
  const isHumanReview =
    po.status === 'PENDING_APPROVAL' ||
    (typeof confidence === 'number' && confidence < 60) ||
    hasExceptions;

  if (isHumanReview) {
    return {
      tone: 'review',
      title: 'Human Review Recommended',
      subtitle: 'Compliance or confidence checks need manual approval before final PO release.',
    };
  }

  return {
    tone: 'approved',
    title: 'Auto-Approval Eligible',
    subtitle: 'Checks are within acceptable thresholds for automated processing.',
  };
}

function getVendorRowTone(vendor) {
  const total = Number(vendor?.total_score ?? 0);
  const delivery = Number(vendor?.delivery_score ?? 0);
  const price = Number(vendor?.price_score ?? 0);

  if (total < 40 || delivery < 40 || price === 0) {
    return 'fail';
  }
  if (total < 55 || delivery < 60 || price < 40) {
    return 'warn';
  }
  return 'pass';
}

const ProcureAIApp = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requisitions');

  const [prStatus, setPrStatus] = useState('APPROVED');
  const [poStatus, setPoStatus] = useState('CREATED');
  const [prQuery, setPrQuery] = useState('');
  const [poQuery, setPoQuery] = useState('');
  const [prLimit, setPrLimit] = useState(50);
  const [poLimit, setPoLimit] = useState(50);
  const [prOffset, setPrOffset] = useState(0);
  const [poOffset, setPoOffset] = useState(0);

  const [prs, setPrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [summary, setSummary] = useState(null);
  const [syncStats, setSyncStats] = useState(null);
  const [batchStats, setBatchStats] = useState(null);

  const [selectedPo, setSelectedPo] = useState(null);
  const [loading, setLoading] = useState({
    sync: false,
    prs: false,
    convertPr: null,
    pos: false,
    summary: false,
    batch: false,
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const requisitionRows = useMemo(() => (Array.isArray(prs) ? prs : []), [prs]);
  const orderRows = useMemo(() => (Array.isArray(pos) ? pos : []), [pos]);
  const filteredRequisitionRows = useMemo(() => {
    const query = prQuery.trim().toLowerCase();
    if (!query) {
      return requisitionRows;
    }
    return requisitionRows.filter((row) =>
      [
        row.pr_number,
        row.pr_item,
        row.material,
        row.material_description,
        row.status,
        row.plant,
        row.requesting_department,
      ]
        .filter(Boolean)
        .some((cell) => String(cell).toLowerCase().includes(query))
    );
  }, [requisitionRows, prQuery]);
  const filteredOrderRows = useMemo(() => {
    const query = poQuery.trim().toLowerCase();
    if (!query) {
      return orderRows;
    }
    return orderRows.filter((row) =>
      [
        row.po_number,
        row.pr_number,
        row.vendor_id,
        row.vendor_name,
        row.status,
        row.conversion_mode,
        row.created_by,
      ]
        .filter(Boolean)
        .some((cell) => String(cell).toLowerCase().includes(query))
    );
  }, [orderRows, poQuery]);
  const confidenceScore = selectedPo?.ai_analysis?.confidence_score;
  const decisionSummary = useMemo(() => getDecisionSummary(selectedPo), [selectedPo]);
  const analysisChecks = useMemo(
    () => getAnalysisChecks(selectedPo?.ai_analysis),
    [selectedPo?.ai_analysis]
  );
  const failedChecks = useMemo(
    () => analysisChecks.filter((check) => check.status !== 'pass'),
    [analysisChecks]
  );
  const reasoningSections = useMemo(
    () => parseReasoningSections(selectedPo?.ai_analysis?.reasoning || ''),
    [selectedPo?.ai_analysis?.reasoning]
  );
  const prPage = Math.floor(prOffset / prLimit) + 1;
  const poPage = Math.floor(poOffset / poLimit) + 1;
  const hasNextPrPage = requisitionRows.length === prLimit;
  const hasNextPoPage = orderRows.length === poLimit;

  const loadPrs = async (options = {}) => {
    const status = options.status ?? prStatus;
    const limit = options.limit ?? prLimit;
    const offset = options.offset ?? prOffset;
    setLoading((prev) => ({ ...prev, prs: true }));
    setError('');
    try {
      const data = await listPurchaseRequisitions({ status, limit, offset });
      setPrs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load purchase requisitions.');
    } finally {
      setLoading((prev) => ({ ...prev, prs: false }));
    }
  };

  const loadPos = async (options = {}) => {
    const status = options.status ?? poStatus;
    const limit = options.limit ?? poLimit;
    const offset = options.offset ?? poOffset;
    setLoading((prev) => ({ ...prev, pos: true }));
    setError('');
    try {
      const data = await listPurchaseOrders({ status, limit, offset });
      setPos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load purchase orders.');
    } finally {
      setLoading((prev) => ({ ...prev, pos: false }));
    }
  };

  const loadSummary = async () => {
    setLoading((prev) => ({ ...prev, summary: true }));
    setError('');
    try {
      const data = await getPoSummaryReport();
      setSummary(data || null);
    } catch (err) {
      setError(err.message || 'Failed to load summary report.');
    } finally {
      setLoading((prev) => ({ ...prev, summary: false }));
    }
  };

  const handleSync = async () => {
    setLoading((prev) => ({ ...prev, sync: true }));
    setError('');
    setNotice('');
    try {
      const data = await syncPurchaseRequisitions();
      setSyncStats(data);
      setNotice(`SAP sync complete. Synced ${data?.synced ?? 0} PRs.`);
      await loadPrs({ status: prStatus, limit: prLimit, offset: prOffset });
    } catch (err) {
      setError(err.message || 'Failed to sync PRs from SAP.');
    } finally {
      setLoading((prev) => ({ ...prev, sync: false }));
    }
  };

  const handleConvertPr = async (prNumber) => {
    setLoading((prev) => ({ ...prev, convertPr: prNumber }));
    setError('');
    setNotice('');
    try {
      const data = await convertPurchaseRequisitionToPo(prNumber);
      setSelectedPo(data);
      setNotice(`PO ${data?.po_number || ''} created from PR ${prNumber}.`.trim());
      await Promise.all([
        loadPrs({ status: prStatus, limit: prLimit, offset: prOffset }),
        loadPos({ status: poStatus, limit: poLimit, offset: poOffset }),
      ]);
      setActiveTab('orders');
    } catch (err) {
      setError(err.message || `Failed to convert PR ${prNumber}.`);
    } finally {
      setLoading((prev) => ({ ...prev, convertPr: null }));
    }
  };

  const handleBatchProcess = async () => {
    setLoading((prev) => ({ ...prev, batch: true }));
    setError('');
    setNotice('');
    try {
      const data = await batchProcessApprovedPrs();
      setBatchStats(data || null);
      setNotice('Batch processing complete. Review the processing summary below.');
      await Promise.all([
        loadPrs({ status: 'APPROVED', limit: prLimit, offset: 0 }),
        loadPos({ status: 'CREATED', limit: poLimit, offset: 0 }),
        loadSummary(),
      ]);
      setPrOffset(0);
      setPoOffset(0);
      setPrStatus('APPROVED');
      setPoStatus('CREATED');
    } catch (err) {
      setError(err.message || 'Failed to run batch processing.');
    } finally {
      setLoading((prev) => ({ ...prev, batch: false }));
    }
  };

  useEffect(() => {
    loadPrs({ status: 'APPROVED', offset: 0, limit: 50 });
    loadPos({ status: 'CREATED', offset: 0, limit: 50 });
    loadSummary();
    // This initial data load should run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportPrCsv = () => {
    const csv = toCsv(filteredRequisitionRows, [
      { label: 'PR Number', value: (row) => row.pr_number },
      { label: 'PR Item', value: (row) => row.pr_item },
      { label: 'Material', value: (row) => row.material },
      { label: 'Material Description', value: (row) => row.material_description },
      { label: 'Quantity', value: (row) => row.quantity },
      { label: 'Unit', value: (row) => row.unit },
      { label: 'Delivery Date', value: (row) => row.delivery_date },
      { label: 'Plant', value: (row) => row.plant },
      { label: 'Status', value: (row) => row.status },
    ]);
    downloadCsvFile(`purchase-requisitions-page-${prPage}.csv`, csv);
  };

  const handleExportPoCsv = () => {
    const csv = toCsv(filteredOrderRows, [
      { label: 'PO Number', value: (row) => row.po_number },
      { label: 'PR Number', value: (row) => row.pr_number },
      { label: 'Vendor ID', value: (row) => row.vendor_id },
      { label: 'Vendor Name', value: (row) => row.vendor_name },
      { label: 'Total Amount', value: (row) => row.total_amount },
      { label: 'Currency', value: (row) => row.currency },
      { label: 'Status', value: (row) => row.status },
      { label: 'Created By', value: (row) => row.created_by },
      { label: 'Created At', value: (row) => row.created_at },
    ]);
    downloadCsvFile(`purchase-orders-page-${poPage}.csv`, csv);
  };

  const changePrPage = (direction) => {
    const nextOffset = prOffset + direction * prLimit;
    if (nextOffset < 0) {
      return;
    }
    setPrOffset(nextOffset);
    loadPrs({ status: prStatus, limit: prLimit, offset: nextOffset });
  };

  const changePoPage = (direction) => {
    const nextOffset = poOffset + direction * poLimit;
    if (nextOffset < 0) {
      return;
    }
    setPoOffset(nextOffset);
    loadPos({ status: poStatus, limit: poLimit, offset: nextOffset });
  };

  const handlePrintAnalysis = () => {
    window.print();
  };

  const renderRequisitionsTab = () => (
    <div className="procure-grid">
      <section className="procure-panel">
        <div className="procure-panel-head">
          <h3>Approved Purchase Requisitions</h3>
          <div className="procure-actions-inline wrap">
            <div className="procure-search-wrap">
              <FiSearch size={14} />
              <input
                value={prQuery}
                onChange={(e) => setPrQuery(e.target.value)}
                placeholder="Search PR, material, plant"
                className="procure-input"
              />
            </div>
            <select
              value={prStatus}
              onChange={(e) => {
                const status = e.target.value;
                setPrStatus(status);
                setPrOffset(0);
                loadPrs({ status, limit: prLimit, offset: 0 });
              }}
            >
              {PR_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <select
              value={prLimit}
              onChange={(e) => {
                const nextLimit = Number(e.target.value);
                setPrLimit(nextLimit);
                setPrOffset(0);
                loadPrs({ status: prStatus, limit: nextLimit, offset: 0 });
              }}
            >
              {[25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}/page
                </option>
              ))}
            </select>
            <button
              type="button"
              className="procure-btn"
              onClick={() => loadPrs({ status: prStatus, limit: prLimit, offset: prOffset })}
            >
              Refresh
            </button>
            <button type="button" className="procure-btn" onClick={handleExportPrCsv}>
              <FiDownload size={14} />
              Export
            </button>
          </div>
        </div>

        {loading.prs ? (
          <p className="procure-muted">Loading PRs...</p>
        ) : filteredRequisitionRows.length === 0 ? (
          <p className="procure-muted">No requisitions found for status {prStatus}.</p>
        ) : (
          <>
          <div className="procure-table-wrap">
            <table className="procure-table">
              <thead>
                <tr>
                  <th>PR</th>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Delivery</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredRequisitionRows.map((pr) => {
                  const isConverting = loading.convertPr === pr.pr_number;
                  return (
                    <tr key={`${pr.pr_number}-${pr.pr_item}`}>
                      <td>
                        <strong>{pr.pr_number}</strong>
                        <div className="procure-subtext">Item {pr.pr_item}</div>
                      </td>
                      <td>
                        {pr.material_description || '-'}
                        <div className="procure-subtext">{pr.material || '-'}</div>
                      </td>
                      <td>{`${formatQuantity(pr.quantity)} ${pr.unit || ''}`.trim()}</td>
                      <td>{formatDate(pr.delivery_date)}</td>
                      <td><span className="procure-chip neutral">{pr.status}</span></td>
                      <td>
                        <button
                          type="button"
                          className="procure-btn small"
                          onClick={() => handleConvertPr(pr.pr_number)}
                          disabled={isConverting}
                        >
                          {isConverting ? 'Converting...' : 'Create PO'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="procure-pager">
            <button type="button" className="procure-btn small" onClick={() => changePrPage(-1)} disabled={prOffset === 0 || loading.prs}>
              <FiChevronLeft size={13} />
              Prev
            </button>
            <span className="procure-subtext">Page {prPage}</span>
            <button
              type="button"
              className="procure-btn small"
              onClick={() => changePrPage(1)}
              disabled={!hasNextPrPage || loading.prs}
            >
              Next
              <FiChevronRight size={13} />
            </button>
          </div>
          </>
        )}
      </section>

      <section className="procure-panel print-target">
        <div className="procure-panel-head">
          <h3>Latest Conversion Result</h3>
          <button
            type="button"
            className="procure-btn"
            onClick={handlePrintAnalysis}
            disabled={!selectedPo}
          >
            <FiPrinter size={14} />
            Print Analysis
          </button>
        </div>
        {selectedPo ? (
          <div className="procure-conversion">
            <div className="procure-kpis">
              <div>
                <span>PO Number</span>
                <strong>{selectedPo.po_number}</strong>
              </div>
              <div>
                <span>Vendor</span>
                <strong>{selectedPo.vendor_name}</strong>
              </div>
              <div>
                <span>Total</span>
                <strong>{formatCurrency(selectedPo.total_amount, selectedPo.currency)}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{selectedPo.status}</strong>
              </div>
            </div>

            {selectedPo.ai_analysis ? (
              <>
                <div className="procure-ai-head">
                  {failedChecks.length > 0 && (
                    <div className="procure-failed-gates-strip">
                      <strong>Failed Gates</strong>
                      <div className="procure-chip-list">
                        {failedChecks.map((check) => (
                          <span key={check.key} className="procure-chip warning">
                            {check.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {decisionSummary && (
                    <div className={`procure-decision-banner ${decisionSummary.tone}`}>
                      {decisionSummary.tone === 'approved' ? (
                        <FiCheckCircle size={15} />
                      ) : (
                        <FiAlertTriangle size={15} />
                      )}
                      <div>
                        <strong>{decisionSummary.title}</strong>
                        <p>{decisionSummary.subtitle}</p>
                      </div>
                    </div>
                  )}

                  <span className="procure-chip ai score">
                    Confidence {selectedPo.ai_analysis.confidence_score}%
                  </span>

                  {analysisChecks.length > 0 && (
                    <div className="procure-check-grid">
                      {analysisChecks.map((check) => (
                        <article key={check.key} className={`procure-check-card ${check.status}`}>
                          <div className="procure-check-title-row">
                            <strong>{check.label}</strong>
                            <span className={`procure-check-pill ${check.status}`}>
                              {check.status === 'pass' ? 'PASS' : check.status === 'fail' ? 'FAIL' : 'WARN'}
                            </span>
                          </div>
                          <p>{check.message || 'No details available.'}</p>
                        </article>
                      ))}
                    </div>
                  )}

                  <div className="procure-analysis-sections">
                    {(reasoningSections.length > 0
                      ? reasoningSections
                      : [{ title: 'Analysis', content: selectedPo.ai_analysis.reasoning || '' }]
                    ).map((section, index) => (
                      <details
                        key={`${section.title}-${index}`}
                        className="procure-analysis-card"
                        open={index < 2}
                      >
                        <summary>{section.title}</summary>
                        <div className="procure-analysis-body">
                          <div className="procure-markdown">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                table: ({ ...props }) => (
                                  <div className="procure-markdown-table-wrap">
                                    <table {...props} />
                                  </div>
                                ),
                              }}
                            >
                              {section.content}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>

                <h4>Vendor Comparison</h4>
                <div className="procure-table-wrap compact">
                  <table className="procure-table compact">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Total</th>
                        <th>Price</th>
                        <th>Delivery</th>
                        <th>Performance</th>
                        <th>Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedPo.ai_analysis.all_vendors_scored || []).map((vendor) => {
                        const tone = getVendorRowTone(vendor);
                        return (
                        <tr key={vendor.vendor_id} className={`procure-vendor-row ${tone}`}>
                          <td>{vendor.vendor_name}</td>
                          <td>{vendor.total_score}</td>
                          <td>{vendor.price_score}</td>
                          <td>{vendor.delivery_score}</td>
                          <td>{vendor.performance_score}</td>
                          <td>{vendor.risk_score}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <h4>Exceptions</h4>
                <div className="procure-chip-list">
                  {(selectedPo.ai_analysis.exceptions || []).length === 0 ? (
                    <span className="procure-muted">No exceptions</span>
                  ) : (
                    selectedPo.ai_analysis.exceptions.map((item) => (
                      <span key={`${item.code}-${item.field || 'none'}`} className="procure-chip warning">
                        <FiAlertTriangle size={12} />
                        {item.code}: {item.message}
                      </span>
                    ))
                  )}
                </div>
              </>
            ) : (
              <p className="procure-muted">
                This PO was created using FIXED_VENDOR mode. No AI analysis payload is returned.
              </p>
            )}

            <div className="procure-kpis">
              <div>
                <span>Delivery Date</span>
                <strong>{formatDate(selectedPo.delivery_date)}</strong>
              </div>
              <div>
                <span>Created At</span>
                <strong>{formatDateTime(selectedPo.created_at)}</strong>
              </div>
            </div>
          </div>
        ) : (
          <p className="procure-muted">Convert a requisition to view AI analysis and PO details.</p>
        )}
      </section>
    </div>
  );

  const renderOrdersTab = () => (
    <section className="procure-panel">
      <div className="procure-panel-head">
        <h3>Purchase Orders</h3>
        <div className="procure-actions-inline wrap">
          <div className="procure-search-wrap">
            <FiSearch size={14} />
            <input
              value={poQuery}
              onChange={(e) => setPoQuery(e.target.value)}
              placeholder="Search PO, PR, vendor"
              className="procure-input"
            />
          </div>
          <select
            value={poStatus}
            onChange={(e) => {
              const status = e.target.value;
              setPoStatus(status);
              setPoOffset(0);
              loadPos({ status, limit: poLimit, offset: 0 });
            }}
          >
            {PO_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <select
            value={poLimit}
            onChange={(e) => {
              const nextLimit = Number(e.target.value);
              setPoLimit(nextLimit);
              setPoOffset(0);
              loadPos({ status: poStatus, limit: nextLimit, offset: 0 });
            }}
          >
            {[25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}/page
              </option>
            ))}
          </select>
          <button
            type="button"
            className="procure-btn"
            onClick={() => loadPos({ status: poStatus, limit: poLimit, offset: poOffset })}
          >
            Refresh
          </button>
          <button type="button" className="procure-btn" onClick={handleExportPoCsv}>
            <FiDownload size={14} />
            Export
          </button>
        </div>
      </div>

      {loading.pos ? (
        <p className="procure-muted">Loading POs...</p>
      ) : filteredOrderRows.length === 0 ? (
        <p className="procure-muted">No purchase orders found for status {poStatus}.</p>
      ) : (
        <>
        <div className="procure-table-wrap">
          <table className="procure-table">
            <thead>
              <tr>
                <th>PO</th>
                <th>PR</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrderRows.map((po) => (
                <tr key={po.po_number}>
                  <td>
                    <strong>{po.po_number}</strong>
                    <div className="procure-subtext">{formatDateTime(po.created_at)}</div>
                  </td>
                  <td>{po.pr_number}</td>
                  <td>{po.vendor_name || po.vendor_id}</td>
                  <td>{formatCurrency(po.total_amount, po.currency)}</td>
                  <td><span className="procure-chip neutral">{po.status}</span></td>
                  <td><span className="procure-chip ai">{po.created_by}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="procure-pager">
          <button type="button" className="procure-btn small" onClick={() => changePoPage(-1)} disabled={poOffset === 0 || loading.pos}>
            <FiChevronLeft size={13} />
            Prev
          </button>
          <span className="procure-subtext">Page {poPage}</span>
          <button
            type="button"
            className="procure-btn small"
            onClick={() => changePoPage(1)}
            disabled={!hasNextPoPage || loading.pos}
          >
            Next
            <FiChevronRight size={13} />
          </button>
        </div>
        </>
      )}
    </section>
  );

  const renderSummaryTab = () => (
    <div className="procure-grid">
      <section className="procure-panel">
        <div className="procure-panel-head">
          <h3>PO Summary Report</h3>
          <button type="button" className="procure-btn" onClick={loadSummary}>
            Refresh Report
          </button>
        </div>

        {loading.summary ? (
          <p className="procure-muted">Loading summary...</p>
        ) : summary ? (
          <div className="procure-kpis report">
            <div>
              <span>Generated At</span>
              <strong>{formatDateTime(summary.generated_at)}</strong>
            </div>
            <div>
              <span>PO Count</span>
              <strong>{summary.po_count ?? 0}</strong>
            </div>
            <div>
              <span>Total Value</span>
              <strong>{formatCurrency(summary.total_value, summary.currency)}</strong>
            </div>
            <div>
              <span>Currency</span>
              <strong>{summary.currency || '-'}</strong>
            </div>
          </div>
        ) : (
          <p className="procure-muted">No summary report available.</p>
        )}
      </section>

      <section className="procure-panel">
        <div className="procure-panel-head">
          <h3>Batch Process Approved PRs</h3>
          <button
            type="button"
            className="procure-btn critical"
            onClick={handleBatchProcess}
            disabled={loading.batch}
          >
            {loading.batch ? 'Processing...' : 'Run Batch Process'}
          </button>
        </div>

        {batchStats ? (
          <>
            <div className="procure-kpis report">
              <div>
                <span>Total PRs</span>
                <strong>{batchStats.total_prs}</strong>
              </div>
              <div>
                <span>Auto Approved</span>
                <strong>{batchStats.auto_approved}</strong>
              </div>
              <div>
                <span>Flagged</span>
                <strong>{batchStats.flagged_for_approval}</strong>
              </div>
              <div>
                <span>Errors</span>
                <strong>{batchStats.errors}</strong>
              </div>
              <div>
                <span>Processing Time</span>
                <strong>{batchStats.processing_time_ms ? `${batchStats.processing_time_ms} ms` : '-'}</strong>
              </div>
            </div>

            <h4>Created PO Numbers</h4>
            <p className="procure-list-line">
              {(batchStats.po_numbers || []).length > 0
                ? batchStats.po_numbers.join(', ')
                : 'No POs created in this run.'}
            </p>

            {(batchStats.error_details || []).length > 0 && (
              <>
                <h4>Error Details</h4>
                <ul className="procure-error-list">
                  {batchStats.error_details.map((item) => (
                    <li key={`${item.pr_number}-${item.error}`}>
                      PR {item.pr_number}: {item.error}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        ) : (
          <p className="procure-muted">Run batch process to see auto-approved and flagged PR counts.</p>
        )}
      </section>
    </div>
  );

  return (
    <motion.div
      className="procure-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <motion.div
        className="procure-header"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="procure-header-left">
          <button type="button" className="procure-back-btn" onClick={() => navigate('/home')}>
            <FiArrowLeft size={15} />
            <span>Back to Home</span>
          </button>
          <div className="procure-header-title">
            <h1>ProcureAI</h1>
            <p>Sync SAP PRs, convert to POs, and inspect AI recommendation details</p>
          </div>
        </div>

        <div className="procure-top-actions">
          <button type="button" className="procure-btn" onClick={handleSync} disabled={loading.sync}>
            <FiRefreshCw size={14} className={loading.sync ? 'spin' : ''} />
            {loading.sync ? 'Syncing...' : 'Sync PRs from SAP'}
          </button>
        </div>
      </motion.div>

      {(error || notice) && (
        <div className={`procure-alert ${error ? 'error' : 'success'}`}>
          {error ? <FiAlertTriangle size={15} /> : <FiCheckCircle size={15} />}
          <span>{error || notice}</span>
        </div>
      )}

      {syncStats && (
        <div className="procure-sync-card">
          <strong>Last Sync:</strong>
          <span>Synced {syncStats.synced ?? 0}</span>
          <span>Skipped {syncStats.skipped ?? 0}</span>
          <span>Errors {(syncStats.errors || []).length}</span>
        </div>
      )}

      <section className="procure-overview-grid" aria-label="ProcureAI overview">
        <article className="procure-overview-card">
          <div className="procure-overview-icon requisitions">
            <FiLayers size={17} />
          </div>
          <div>
            <span>Visible PRs</span>
            <strong>{requisitionRows.length}</strong>
          </div>
        </article>
        <article className="procure-overview-card">
          <div className="procure-overview-icon orders">
            <FiPackage size={17} />
          </div>
          <div>
            <span>Visible POs</span>
            <strong>{orderRows.length}</strong>
          </div>
        </article>
        <article className="procure-overview-card">
          <div className="procure-overview-icon summary">
            <FiActivity size={17} />
          </div>
          <div>
            <span>Summary PO Count</span>
            <strong>{summary?.po_count ?? '-'}</strong>
          </div>
        </article>
        <article className="procure-overview-card">
          <div className="procure-overview-icon confidence">
            <FiCpu size={17} />
          </div>
          <div>
            <span>Latest AI Confidence</span>
            <strong>{confidenceScore === undefined ? '-' : `${confidenceScore}%`}</strong>
          </div>
        </article>
      </section>

      <motion.div
        className="procure-tab-bar"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14, duration: 0.32 }}
      >
        {TABS.map((tab, idx) => (
          <motion.button
            key={tab.id}
            type="button"
            className={`procure-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + idx * 0.05 }}
            whileTap={{ scale: 0.97 }}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {activeTab === tab.id && <FiTrendingUp size={12} />}
          </motion.button>
        ))}
      </motion.div>

      <div className="procure-content">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} variants={pageVariants} initial="initial" animate="animate" exit="exit">
            {activeTab === 'requisitions' && renderRequisitionsTab()}
            {activeTab === 'orders' && renderOrdersTab()}
            {activeTab === 'summary' && renderSummaryTab()}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default ProcureAIApp;
